import { redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { toResult } from "#/modules/util.auth/api-errors";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { getPrice } from "#/modules/generic.master-data/api.master-data.server";
import { getSchoolMetaData } from "#/modules/school/api.school.server";
import { translateApiError } from "#/modules/util.errors/translate";
import { client } from "#/modules/util.fetch-client/fetch-client.server";

/** Letterlijke teksten van de checkboxes – worden als consentText naar de API gestuurd. */
const CONSENT_TEXT_BEVOEGDHEID =
  "Ik verklaar dat ik bevoegd ben om namens het bedrijf deze overeenkomst aan te gaan en dat mijn directie of bevoegde leidinggevende op de hoogte is en hiermee heeft ingestemd.";
const CONSENT_TEXT_ALGEMENE_VOORWAARDEN = "Ik ga akkoord met de algemene voorwaarden";

interface GetEndTestPeriodInput {
  success: boolean;
  locale: string;
  school: string;
  schoolyear?: string;
}

export const getEndTestPeriodData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: GetEndTestPeriodInput) => data)
  .handler(async ({ data }) => {
    const [metadataResult, priceResult] = await Promise.all([getSchoolMetaData(), getPrice()]);
    if (Result.isError(metadataResult)) throw metadataResult.error;
    const schoolMetadata = metadataResult.value;
    const { showSubscriptionReminder, subscriptionInProgress } = schoolMetadata;

    if (!showSubscriptionReminder && !subscriptionInProgress) {
      if (!data.success) {
        throw redirect({
          to: "/$locale/{-$schoolyear}/$school/end-test-period",
          params: { locale: data.locale, schoolyear: data.schoolyear, school: data.school },
          search: { success: true },
        });
      }
    }

    // Price failures collapse to null (the page renders without a price).
    const price = priceResult
      .map((value) => (value ? { price: value.price, discountPrice: value.discountPrice } : null))
      .unwrapOr(null);

    return {
      subscriptionInProgress,
      schoolMetadata,
      price,
    };
  });

export const submitSchoolSubscription = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: { bevoegdheid: boolean; algemeneVoorwaarden: boolean }) => data)
  .handler(async ({ data }) => {
    if (!data.bevoegdheid || !data.algemeneVoorwaarden) {
      return { formError: "Vink beide checkboxes aan om door te gaan." };
    }

    const consentText = [CONSENT_TEXT_BEVOEGDHEID, CONSENT_TEXT_ALGEMENE_VOORWAARDEN].join("\n\n");

    const subscribeResult = toResult(
      await client.POST("/school/licentie/abonneren", {
        body: { consentText },
      }),
    );

    if (Result.isError(subscribeResult)) {
      return { formError: translateApiError(subscribeResult.error) };
    }

    const metadataResult = await getSchoolMetaData();
    if (Result.isError(metadataResult)) throw metadataResult.error;
    return { subscriptionInProgress: metadataResult.value.subscriptionInProgress };
  });
